// Ubicación en el repo: src/lib/dashboard.js
// Agregación de datos para la pantalla de Inicio/Dashboard — replica
// renderDashboard() del prototipo (línea ~714): 4 tarjetas numéricas,
// tendencia de 3/6/12 meses (citas + ventas), una dona con la distribución
// de estados de las citas del mes, y un bloque de alertas que cambia según
// el módulo activo (ORL: stock bajo; OFT: sin alertas propias por ahora).
// Todo escapado por `branchId`/`module` igual que el resto de la app — RLS
// ya filtra por acceso real, esto es solo para no mezclar
// sucursales/módulos en la misma vista.
//
// "Controles de audífono próximos/vencidos" y "pacientes que no compraron
// lentes este mes" salieron de acá (ver DECISIONS.md #44, addendum): el
// usuario los vio poco útiles para el dashboard — los controles ya se ven
// en Agenda, y "no compraron" era un dato muy puntual para un panel entero.
// Se reemplazaron por la dona de estados de citas, que sirve igual para
// ambos módulos.
import { supabase } from './supabaseClient';
import { listAppointmentsInRange, BLOCK_TYPES, STATUS_FLOW, STATUS_COLORS } from './appointments';
import { listCatalog } from './catalog';
import { todayISO } from './format';

// Mismo `lastNMonths()` del prototipo (línea ~681): array de 'YYYY-MM',
// del más viejo al más reciente, terminando en el mes actual.
export function lastNMonths(n) {
  const now = new Date();
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function monthsAgoStartISO(n) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// `patients(patient_branches(branch_id))` es el mismo patrón en dos capas
// que usan Exams.jsx/Prescriptions.jsx/OftSales.jsx: RLS ya restringe por
// patient_branch_match, esto solo acota a la sucursal del Dashboard.
function filterByBranch(rows, branchId) {
  return rows.filter((r) => (r.patients?.patient_branches ?? []).some((pb) => pb.branch_id === branchId));
}

async function getOrlData({ branchId, monthStr, months, rangeStart }) {
  const [examsResult, hearingaidsResult, catalog] = await Promise.all([
    supabase
      .from('exams')
      .select('id, date, patients(patient_branches(branch_id))')
      .is('deleted_at', null)
      .gte('date', rangeStart),
    supabase
      .from('hearingaids')
      .select('id, purchase_date')
      .eq('branch_id', branchId)
      .is('deleted_at', null)
      .gte('purchase_date', rangeStart),
    // branchId acá: la alerta de stock bajo es por sucursal desde la Fase B
    // (migración 0049) — antes era un número global compartido.
    listCatalog({ branchId }),
  ]);
  if (examsResult.error) throw examsResult.error;
  if (hearingaidsResult.error) throw hearingaidsResult.error;

  const exams = filterByBranch(examsResult.data ?? [], branchId);
  const sales = hearingaidsResult.data ?? [];

  return {
    secondCard: { value: exams.filter((e) => e.date.startsWith(monthStr)).length, label: 'Audiometrías este mes' },
    thirdCard: { value: sales.filter((h) => h.purchase_date.startsWith(monthStr)).length, label: 'Ventas este mes' },
    ventasTrend: months.map((m) => sales.filter((h) => h.purchase_date.startsWith(m)).length),
    alerts: {
      type: 'ORL',
      lowStock: catalog.filter((c) => Number(c.stock || 0) <= Number(c.stock_min ?? 2)),
    },
  };
}

async function getOftData({ branchId, monthStr, months, rangeStart }) {
  const [rxResult, salesResult] = await Promise.all([
    supabase
      .from('prescriptions')
      .select('id, date, patients(patient_branches(branch_id))')
      .is('deleted_at', null)
      .gte('date', rangeStart),
    supabase
      .from('oftsales')
      .select(
        'id, date, compro, patients(id, nombre, apellido, segundo_nombre, segundo_apellido, patient_branches(branch_id))'
      )
      .is('deleted_at', null)
      .gte('date', rangeStart),
  ]);
  if (rxResult.error) throw rxResult.error;
  if (salesResult.error) throw salesResult.error;

  const rx = filterByBranch(rxResult.data ?? [], branchId);
  const sales = filterByBranch(salesResult.data ?? [], branchId);
  const compraron = sales.filter((s) => s.compro);

  return {
    secondCard: { value: rx.filter((r) => r.date.startsWith(monthStr)).length, label: 'Recetas este mes' },
    thirdCard: { value: compraron.filter((s) => s.date.startsWith(monthStr)).length, label: 'Ventas de lentes este mes' },
    ventasTrend: months.map((m) => compraron.filter((s) => s.date.startsWith(m)).length),
    alerts: { type: 'OFT' },
  };
}

// Dona "Estado de citas del mes" (DECISIONS.md #44 addendum): distribución
// de `status` de las citas del mes actual, mismo orden/colores que
// STATUS_FLOW/STATUS_COLORS (Agenda.jsx) para no inventar una paleta nueva.
// Se excluyen los BLOCK_TYPES (bloqueos de agenda, no son "citas" de
// paciente) — mismo criterio que ya usa citasTrend.
function statusDistribution(appointments, monthStr) {
  const thisMonth = appointments.filter((a) => a.date.startsWith(monthStr) && !BLOCK_TYPES.includes(a.type));
  return STATUS_FLOW.map((status) => ({
    label: status,
    color: STATUS_COLORS[status],
    value: thisMonth.filter((a) => a.status === status).length,
  }));
}

export async function getDashboardData({ branchId, module }) {
  if (!branchId || !module) return null;
  const today = todayISO();
  const monthStr = today.slice(0, 7);
  const months = lastNMonths(12);
  const rangeStart = monthsAgoStartISO(11); // incluye el mes actual = 12 meses en total

  const [appointments, moduleData] = await Promise.all([
    listAppointmentsInRange({ branchId, module, startDate: rangeStart, endDate: today }),
    module === 'ORL'
      ? getOrlData({ branchId, monthStr, months, rangeStart })
      : getOftData({ branchId, monthStr, months, rangeStart }),
  ]);

  return {
    aptsThisMonthCount: appointments.filter((a) => a.date.startsWith(monthStr)).length,
    aptsTodayCount: appointments.filter((a) => a.date === today).length,
    months,
    citasTrend: months.map(
      (m) => appointments.filter((a) => a.date.startsWith(m) && !BLOCK_TYPES.includes(a.type)).length
    ),
    statusDistribution: statusDistribution(appointments, monthStr),
    ...moduleData,
  };
}
